import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { globalPttService } from '../services/ptt/globalPttService';
import type { GlobalPttState, PttSpeaker } from '../services/ptt/types';

type PttContextValue = GlobalPttState & {
  getRemoteSpeakerForChannel: (channelId: string | null) => PttSpeaker | null;
  cutIncomingWalkieTalkie: (cutterDisplayName?: string) => void;
  startTransmit: (channelId: string, displayName: string) => Promise<void>;
  stopTransmit: () => void;
};

const defaultState: GlobalPttState = {
  remoteSpeaker: null,
  isReceiving: false,
  isPlayingLocally: false,
  isTransmitting: false,
  transmitChannelId: null,
  micError: null,
};

const PttContext = createContext<PttContextValue>({
  ...defaultState,
  getRemoteSpeakerForChannel: () => null,
  cutIncomingWalkieTalkie: () => {},
  startTransmit: async () => {},
  stopTransmit: () => {},
});

export function usePtt(): PttContextValue {
  return useContext(PttContext);
}

export const PttProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<GlobalPttState>(globalPttService.getState());

  useEffect(() => globalPttService.subscribe(setState), []);

  const value = useMemo<PttContextValue>(
    () => ({
      ...state,
      getRemoteSpeakerForChannel: (channelId) =>
        globalPttService.getRemoteSpeakerForChannel(channelId),
      cutIncomingWalkieTalkie: (cutterDisplayName) =>
        globalPttService.cutIncoming(cutterDisplayName),
      startTransmit: (channelId, displayName) =>
        globalPttService.startTransmit(channelId, displayName),
      stopTransmit: () => globalPttService.stopTransmit(),
    }),
    [state]
  );

  return <PttContext.Provider value={value}>{children}</PttContext.Provider>;
};
